/*
  WLAM – quà tặng theo giá trị đơn (xem snippets/wl-gift.liquid).
  Bậc 1 (từ 1 triệu): 1 quà, khách chọn móc khóa hoặc vớ; tự thêm móc khóa nếu khách chưa chọn.
  Bậc 2 (từ 2 triệu): móc khóa + 1 đôi vớ (vớ còn nhiều hàng hơn được thêm trước, khách đổi được).
  Dưới 1 triệu: bỏ quà ra khỏi giỏ. Chỉ đụng tới dòng quà do theme thêm (thuộc tính _gift).
  Khách tự xóa quà thì không tự thêm lại trong phiên đó, trừ khi khách bấm chọn lại.
*/
import { Cart } from './cart.js';
import { events, formatMoney } from './theme.js';

const routes = window.Atelier?.routes || {};
const DECLINED = 'wl-gift-declined';
let busy = false;

const read = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {}
};

class WlGift extends HTMLElement {
  connectedCallback() {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    this.sub = Number(this.dataset.sub) || 0;
    this.t1 = Math.round(Number(this.dataset.t1) * rate);
    this.t2 = Math.round(Number(this.dataset.t2) * rate);
    try {
      this.lines = JSON.parse(this.dataset.lines || '[]');
    } catch {
      this.lines = [];
    }
    this.opts = Array.from(this.querySelectorAll('[data-v]')).map((el) => ({
      el,
      v: Number(el.dataset.v),
      kind: el.dataset.kind,
      stock: Number(el.dataset.stock) || 0,
      out: el.hasAttribute('data-out'),
    }));
    this.lines = this.lines.filter((l) => this.kindOf(l.v));
    this.tier = this.sub >= this.t2 ? 2 : this.sub >= this.t1 ? 1 : 0;
    this.addEventListener('click', this.onClick);
    this.paint();
    if (!busy) this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  kindOf(v) {
    return this.opts.find((o) => o.v === Number(v))?.kind;
  }

  paint() {
    const { tier, sub, t1, t2 } = this;
    const chosen = new Set(this.lines.map((l) => l.v));
    const offered = this.opts.filter((o) => !o.out || chosen.has(o.v));
    if (!offered.length) {
      this.hidden = true;
      return;
    }
    const remaining = Math.max(0, tier === 0 ? t1 - sub : tier === 1 ? t2 - sub : 0);
    const msg = this.querySelector('[data-msg]');
    if (msg) msg.textContent = (this.getAttribute(`data-text-${tier}`) || '').replace('[amount]', formatMoney(remaining));
    const pct = t2 > 0 ? Math.min(100, (sub / t2) * 100) : 0;
    const fill = this.querySelector('[data-fill]');
    if (fill) fill.style.width = `${pct}%`;
    this.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(Math.round(pct)));
    this.classList.toggle('is-t1', tier >= 1);
    this.classList.toggle('is-t2', tier === 2);
    const box = this.querySelector('[data-opts]');
    if (box) box.hidden = tier === 0;
    this.opts.forEach((o) => {
      const on = chosen.has(o.v);
      o.el.hidden = o.out && !on;
      o.el.setAttribute('aria-pressed', String(on));
      const state = o.el.querySelector('[data-on]');
      if (state) state.textContent = on ? state.dataset.on : state.dataset.off;
    });
    this.hidden = false;
  }

  bestSock(list) {
    return list.filter((o) => o.kind === 'sock').sort((a, b) => b.stock - a.stock)[0];
  }

  sync() {
    const { tier } = this;
    const declined = read(DECLINED) === String(tier);
    if (tier === 0) write(DECLINED, null);
    const updates = {};
    const add = [];
    if (tier === 0) {
      this.lines.forEach((l) => (updates[l.k] = 0));
    } else {
      const keep =
        tier === 1
          ? this.lines.slice(0, 1)
          : [
              this.lines.find((l) => this.kindOf(l.v) === 'key'),
              this.lines.find((l) => this.kindOf(l.v) === 'sock'),
            ].filter(Boolean);
      this.lines.forEach((l) => {
        if (!keep.includes(l)) updates[l.k] = 0;
        else if (l.q > 1) updates[l.k] = 1;
      });
      if (!declined) {
        const avail = this.opts.filter((o) => !o.out);
        const hasKey = keep.some((l) => this.kindOf(l.v) === 'key');
        const hasSock = keep.some((l) => this.kindOf(l.v) === 'sock');
        if (tier === 1 && !keep.length) {
          const pick = avail.find((o) => o.kind === 'key') || this.bestSock(avail);
          if (pick) add.push(pick.v);
        }
        if (tier === 2) {
          const key = avail.find((o) => o.kind === 'key');
          const sock = this.bestSock(avail);
          if (!hasKey && key) add.push(key.v);
          if (!hasSock && sock) add.push(sock.v);
        }
      }
    }
    if (Object.keys(updates).length || add.length) this.apply(updates, add);
  }

  onClick = (e) => {
    const btn = e.target.closest('[data-v]');
    if (!btn || busy || this.tier === 0) return;
    const v = Number(btn.dataset.v);
    if (this.lines.some((l) => l.v === v)) return;
    const kind = btn.dataset.kind;
    const updates = {};
    this.lines.forEach((l) => {
      if (this.tier === 1 || this.kindOf(l.v) === kind) updates[l.k] = 0;
    });
    write(DECLINED, null);
    this.apply(updates, [v]);
  };

  async apply(updates, add) {
    if (busy) return;
    busy = true;
    try {
      let cart;
      if (Object.keys(updates).length) cart = await Cart.update({ updates });
      if (add.length) {
        cart = await Cart.post(`${routes.cart_add_url}.js`, {
          items: add.map((id) => ({ id, quantity: 1, properties: { _gift: 'true' } })),
        });
      }
      if (cart) events.emit('cart:updated', { cart });
    } catch {
    } finally {
      busy = false;
    }
  }
}

if (!customElements.get('wl-gift')) {
  customElements.define('wl-gift', WlGift);

  /* Khách tự xóa dòng quà: ghi nhận để không tự thêm lại ở bậc hiện tại. */
  document.addEventListener(
    'click',
    (e) => {
      const row = e.target.closest('[data-remove-line]')?.closest('[data-line-key]');
      if (!row) return;
      const gift = document.querySelector('wl-gift');
      if (!gift?.lines?.some((l) => l.k === row.dataset.lineKey)) return;
      write(DECLINED, String(gift.tier));
    },
    true
  );
}
